import { LayoutTemplate, RefreshCw, MousePointerClick, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useInquiry } from "@/components/InquiryModal";
import brand from "@/config/brand";

const ICONS = {
  layout: LayoutTemplate,
  redesign: RefreshCw,
  features: MousePointerClick,
  care: ShieldCheck,
};

const Services = () => {
  const { open } = useInquiry();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-charcoal py-20 md:py-28 lg:py-32 overflow-hidden"
    >
      <div className="noise-overlay" aria-hidden="true" />
      <div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-turquoise/10 blur-[150px] pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-champagne mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-turquoise inline-block" />
            Services & Quotes
          </p>
          <h2
            data-testid="services-headline"
            className="font-serif font-medium tracking-tight text-cream text-2xl sm:text-3xl lg:text-4xl leading-tight"
          >
            Beautiful Websites. <span className="italic text-turquoise">Built for Your Business.</span>
          </h2>
          <p className="mt-5 font-sans text-sm sm:text-base text-champagne/60 leading-relaxed">
            Whether you're starting from scratch, refreshing an existing website, or adding powerful
            new features, we'll create a solution tailored to your business.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {brand.services.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.id} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`service-card-${s.id}`}
                  className="h-full flex flex-col bg-ink2 border border-champagne/15 rounded-2xl p-8 hover:border-turquoise/60 hover:-translate-y-1.5 transition-[border-color,transform,box-shadow] duration-300 hover:shadow-[0_24px_48px_-16px_rgba(0,175,163,0.25)]"
                >
                  <div className="w-12 h-12 rounded-full bg-turquoise/10 border border-turquoise/30 flex items-center justify-center mb-6">
                    <Icon size={20} className="text-turquoise" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-serif font-medium text-xl text-cream leading-snug mb-3">
                    {s.title}
                  </h3>
                  <p className="font-sans text-sm text-champagne/60 leading-relaxed mb-7">{s.text}</p>
                  <button
                    data-testid={`service-quote-button-${s.id}`}
                    onClick={() => open(s.preset)}
                    className="group mt-auto inline-flex items-center gap-2 w-fit border border-champagne/30 text-cream hover:border-turquoise hover:text-turquoise px-5 py-2.5 rounded-full font-sans text-sm transition-colors duration-300"
                  >
                    Request a Quote
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2} className="mt-12">
          <p
            data-testid="services-hosting-statement"
            className="font-sans text-sm text-champagne/60 leading-relaxed border-l-2 border-turquoise/50 pl-5 max-w-4xl"
          >
            {brand.hostingStatement}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Services;
